/**
 * <StudentSettingsProfilePage> — « Profil », la troisième page des paramètres de l'élève (page).
 *
 * Les douze champs du profil pédagogique d'aujourd'hui, tous gardés, dans leur ordre et avec
 * leurs mots, sous le rappel de l'avancement du profil. Chaque choix s'ouvre en feuille
 * (`<SelectField>`), comme aujourd'hui : le champ montre toujours la réponse en cours, que les
 * options soient nombreuses (un sport) ou longues (une phrase).
 *
 * « Mes matières de spécialité » n'existe qu'en 1ère et en Tle, et suit la classe choisie dans le
 * formulaire, pas celle du compte.
 *
 * À ne pas perdre au câblage : quand c'est l'élève qui enregistre, le champ Classe AFFIRME sa
 * classe (`StudentProfile::affirmClass()`).
 */
import React from "react"

import { ProgressBar } from "../../atoms/ProgressBar"
import { PROGRESS_BAR_HEIGHT } from "../../atoms/ProgressBar/ProgressBar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SelectField, type UISelectOption } from "../../molecules/SelectField"
import {
    AccountFormCardSkeleton,
    AccountSettingsTemplate,
    type AccountSettingsShellProps,
} from "../../templates/AccountSettingsTemplate"
import { type UIStudentProfileSettings, type UIStudentProfileCatalogs } from "../../../ports/primary/ui/types"

export type { UIStudentProfileSettings, UIStudentProfileCatalogs }

export type StudentProfileSettingsField = keyof UIStudentProfileSettings

type SingleField = "classId" | "sportId" | "reasonId" | "subjectPriorityId" | "workFrequencyId" | "workDurationId"
type MultipleField = "specialtySubjectIds" | "qualityIds" | "appreciatedSubjectIds"

export type StudentSettingsProfilePageProps = AccountSettingsShellProps & {
    /** `null` tant que le compte n'est pas là. */
    values: UIStudentProfileSettings | null
    errors?: Partial<Record<StudentProfileSettingsField, string>>
    onChange: <K extends StudentProfileSettingsField>(field: K, value: UIStudentProfileSettings[K]) => void
    catalogs: UIStudentProfileCatalogs
    /** L'avancement du profil, de 0 à 100. */
    profileCompletion: number
    /** Ouvre la feuille d'un champ au premier rendu (stories). */
    initialOpenField?: SingleField | MultipleField
}

/** Les classes où l'élève a des spécialités. */
const SPECIALTY_CLASSES = ["1ère", "Tle"]

const optionId = (option: UISelectOption) => option.id
const optionLabel = (option: UISelectOption) => option.label

function ProfileSkeleton() {
    return (
        <Stack gap="4">
            <Card p="4">
                <Stack gap="2">
                    <Text weight="semibold">Profil complété à</Text>
                    <Skeleton shape="block" height={PROGRESS_BAR_HEIGHT} radius="sm" />
                </Stack>
            </Card>
            <AccountFormCardSkeleton fields={6} />
        </Stack>
    )
}

export function StudentSettingsProfilePage(props: StudentSettingsProfilePageProps) {
    const { values, errors = {}, onChange, catalogs, profileCompletion, initialOpenField, ...shell } = props

    const busy = shell.submitting === true
    const complete = profileCompletion >= 100

    const single = (name: SingleField, label: string, items: UISelectOption[]) =>
        values != null && (
            <SelectField
                label={label}
                items={items}
                getItemId={optionId}
                getItemLabel={optionLabel}
                selectedIds={values[name] !== "" ? [values[name]] : []}
                onChange={(ids) => onChange(name, String(ids[0] ?? ""))}
                error={errors[name]}
                disabled={busy}
                defaultOpen={initialOpenField === name}
            />
        )

    const multiple = (name: MultipleField, label: string, items: UISelectOption[]) =>
        values != null && (
            <SelectField
                label={label}
                items={items}
                getItemId={optionId}
                getItemLabel={optionLabel}
                selectedIds={values[name]}
                onChange={(ids) => onChange(name, ids.map(String))}
                multiple
                error={errors[name]}
                disabled={busy}
                defaultOpen={initialOpenField === name}
            />
        )

    return (
        <AccountSettingsTemplate {...shell} title="Profil" skeleton={<ProfileSkeleton />}>
            {values != null && (
                <Stack gap="4">
                    <Card p="4">
                        <Stack gap="2">
                            <Row gap="2" align="center" justify="between">
                                <Text weight="semibold">Profil complété à</Text>
                                <Text weight="bold" color="primary">{`${Math.round(profileCompletion)} %`}</Text>
                            </Row>
                            <ProgressBar value={profileCompletion} color={complete ? "success" : "role"} />
                        </Stack>
                    </Card>

                    <Card p="4">
                        <Stack gap="4">
                            {single("classId", "Classe", catalogs.classes)}
                            {single("sportId", "Quel est ton sport préféré ?", catalogs.sports)}
                            {SPECIALTY_CLASSES.includes(values.classId) &&
                                multiple("specialtySubjectIds", "Mes matières de spécialité", catalogs.subjects)}
                            <TextInput
                                label="Établissement scolaire"
                                placeholder="ex : Lycée Descartes"
                                value={values.schoolName}
                                onChangeText={(text) => onChange("schoolName", text)}
                                error={errors.schoolName}
                                disabled={busy}
                            />
                            {single("reasonId", "En quoi Masteur peut m'être utile ?", catalogs.reasons)}
                            {single(
                                "subjectPriorityId",
                                "Les matières où j'ai le plus besoin de m'améliorer",
                                catalogs.subjectPriorities
                            )}
                            {multiple("qualityIds", "Mes principales qualités", catalogs.qualities)}
                            {multiple(
                                "appreciatedSubjectIds",
                                "Les matières que j'apprécie le plus",
                                catalogs.subjects
                            )}
                            <TextInput
                                label="Le(s) métier(s) où je m'imagine plus tard"
                                placeholder="ex : Médecin, Enseignant..."
                                value={values.futureJob}
                                onChangeText={(text) => onChange("futureJob", text)}
                                error={errors.futureJob}
                                disabled={busy}
                            />
                            <TextInput
                                label="L'orientation que je souhaiterais"
                                placeholder="ex : 1ère ST2S, École d'ingénieur..."
                                value={values.futureOrientation}
                                onChangeText={(text) => onChange("futureOrientation", text)}
                                error={errors.futureOrientation}
                                disabled={busy}
                            />
                            {single(
                                "workFrequencyId",
                                "Globalement en dehors des cours je travaille",
                                catalogs.workFrequencies
                            )}
                            {single(
                                "workDurationId",
                                "Chaque semaine, en dehors des cours, je travaille en moyenne",
                                catalogs.workDurations
                            )}
                        </Stack>
                    </Card>
                </Stack>
            )}
        </AccountSettingsTemplate>
    )
}
